import { gql, MutationResult, useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { errorToast } from 'basics/utils/toast';
import { extractApolloErrorDetails } from 'services/graphql/extractApolloErrorDetails';
import Logger from 'services/logger';

export type ExportQrCodesResponse = {
  exportQrCodes: {
    buffer: string;
  };
};

export type UseExportQrCodesMutation = [
  (eventId?: string) => Promise<string | null>,
  MutationResult<ExportQrCodesResponse>,
];

export const exportQrCodesMutationCall = gql`
mutation exportQrCodes($eventId: String!) {
    exportQrCodes(eventId: $eventId) {
      buffer
    }
}`;

export const useExportQrCodesMutation = (): UseExportQrCodesMutation => {
  const { t } = useTranslation();
  const [exportQrCodesMutation, state] = useMutation<ExportQrCodesResponse>(exportQrCodesMutationCall);

  const executeExportQrCodesMutation = useCallback(async (eventId?: string) => {
    try {
      if (!eventId) {
        errorToast(t('errors_standard'));
        return null;
      }
      const { data } = await exportQrCodesMutation({
        variables: { eventId },
      });

      return data?.exportQrCodes?.buffer || null;
    } catch (error: unknown) {
      if (error instanceof Error) {
        const customContext = extractApolloErrorDetails(error);
        Logger.logError({
          error,
          customContext,
          transactionName: 'exportQrCodes.ts - useExportQrCodesMutation',
        });
        errorToast(t('qr_codes_generation_error'));
      }
      return null;
    }
  }, [exportQrCodesMutation, t]);

  return [executeExportQrCodesMutation, state];
};
